import { motion } from "framer-motion";
import {
  Landmark,
  Megaphone,
  TrendingUp,
  Workflow,
  Users,
  Headset,
  BarChart3,
  ClipboardList,
  CalendarClock,
  PhoneIncoming,
  AudioLines,
  Globe,
  Zap,
} from "lucide-react";
import { SectionHeader, EASE } from "@/components/Reveal";

const AGENTS = [
  { icon: Landmark, name: "Finance AI", desc: "Invoices, cashflow, payroll and VAT — handled with precision.", span: true },
  { icon: Megaphone, name: "Marketing AI", desc: "Campaigns, content and SEO that never stop shipping." },
  { icon: TrendingUp, name: "Sales AI", desc: "Leads qualified, proposals written, deals followed up." },
  { icon: Workflow, name: "Operations AI", desc: "Workflows, dashboards and SOPs running like clockwork." },
  { icon: Users, name: "HR AI", desc: "Recruitment, onboarding and performance on autopilot." },
  { icon: Headset, name: "Customer Support AI", desc: "Every enquiry answered instantly, on every channel." },
  { icon: BarChart3, name: "Analytics AI", desc: "Reports and insights generated before you ask." },
  { icon: ClipboardList, name: "Administration AI", desc: "Documents, data entry and admin — silently done." },
  { icon: CalendarClock, name: "Scheduling AI", desc: "Calendars, bookings and reminders in perfect sync." },
  { icon: PhoneIncoming, name: "Receptionist AI", desc: "Every call answered, routed and booked — 24/7." },
  { icon: AudioLines, name: "Voice Calling AI", desc: "Natural AI phone calls, inbound and outbound." },
  { icon: Globe, name: "Website AI", desc: "Websites built, updated and optimised by AI." },
  { icon: Zap, name: "Automation AI", desc: "Connects every tool, API and workflow into one nervous system.", span: true },
];

export default function Workforce() {
  return (
    <section
      id="workforce"
      data-testid="workforce-section"
      className="relative mx-auto max-w-7xl px-6 py-32 lg:px-10"
    >
      <div className="absolute left-1/2 top-0 h-[400px] w-[700px] -translate-x-1/2 rounded-full bg-[radial-gradient(ellipse_at_center,rgba(216,192,138,0.06),transparent_65%)] blur-3xl" />
      <SectionHeader
        label="The Workforce"
        title={
          <>
            Thirteen Minds. <span className="gold-text">One Company.</span>
          </>
        }
        sub="Deploy specialised AI employees across every department of your business. Each one trained for its role. All of them working as one."
      />

      <div className="mt-16 grid grid-cols-1 gap-4 [grid-auto-flow:dense] sm:grid-cols-2 lg:grid-cols-4">
        {AGENTS.map((a, i) => (
          <motion.div
            key={a.name}
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ duration: 0.8, delay: (i % 4) * 0.08, ease: EASE }}
            whileHover={{ y: -6 }}
            onMouseMove={(e) => {
              const r = e.currentTarget.getBoundingClientRect();
              e.currentTarget.style.setProperty("--mx", `${e.clientX - r.left}px`);
              e.currentTarget.style.setProperty("--my", `${e.clientY - r.top}px`);
            }}
            onClick={() =>
              window.dispatchEvent(
                new CustomEvent("open-concierge", {
                  detail: {
                    message: `Tell me more about ${a.name} — what exactly does it do for my business?`,
                  },
                })
              )
            }
            role="button"
            tabIndex={0}
            onKeyDown={(e) =>
              e.key === "Enter" &&
              window.dispatchEvent(
                new CustomEvent("open-concierge", {
                  detail: {
                    message: `Tell me more about ${a.name} — what exactly does it do for my business?`,
                  },
                })
              )
            }
            className={`card-glow-hover glass-panel group relative cursor-pointer overflow-hidden rounded-2xl p-7 ${
              a.span ? "sm:col-span-2" : ""
            }`}
            data-testid={`agent-card-${a.name.toLowerCase().replace(/\s+/g, "-")}`}
          >
            <div className="flex items-start justify-between">
              <div className="flex h-12 w-12 items-center justify-center rounded-xl border border-gold/20 bg-gold/5 transition-[box-shadow,border-color] duration-500 group-hover:border-gold/50 group-hover:shadow-[0_0_25px_rgba(216,192,138,0.3)]">
                <a.icon className="h-5 w-5 text-gold transition-transform duration-500 group-hover:scale-110" />
              </div>
              <span className="flex items-center gap-1.5 font-mono text-[9px] uppercase tracking-[0.2em] text-mint/70">
                <span className="status-dot" style={{ width: 5, height: 5 }} />
                Online
              </span>
            </div>
            <h3 className="mt-6 font-display text-lg font-medium tracking-tight text-[#F5F5F5]">
              {a.name}
            </h3>
            <p className="mt-2 text-sm leading-relaxed text-[#A3A3A3]">
              {a.desc}
            </p>
            <p className="mt-4 font-mono text-[9px] uppercase tracking-[0.25em] text-gold/0 transition-colors duration-500 group-hover:text-gold/80">
              Tap to ask the concierge
            </p>
          </motion.div>
        ))}
      </div>
    </section>
  );
}
